import React, {useState} from 'react';
import {Button} from '@material-ui/core';
import {NavigationService} from 'services/index';
import {reduxToggleSidePanel} from 'appredux/actions';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {Route} from 'react-router-dom';
import {
  IDepot,
  IGenericResponse,
  ISupplierUserInput,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  apiAddTeamMember,
  apiFindTeamMember,
  apiUpdateTeamMember,
} from 'constants/Api';
import {UserForm} from 'components/UserForm/UserForm';
import {PermissionsService} from 'services/PermissionsService';
import {ESidePanelView, ESupplierUserPermissions} from 'constants/index';

const AddOrUpdateTeamMember = (props: {
  loggedInUser: ISupplierUserProfile;
  user?: ISupplierUserProfile;
  depots: IDepot[];
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyUpdated, setHasSuccessfullyUpdated] = useState(false);

  const isAdding = !props.user;

  const handleSubmit = (user: ISupplierUserInput) => {
    if (!canWrite) return;
    setIsLoading(true);

    const apiCall = isAdding
      ? (user: ISupplierUserInput) =>
          apiAddTeamMember(user).then((response) => {
            if (!response.success) return response;
            return handleOpenPermissionsEdit(user);
          })
      : (user: ISupplierUserInput) => apiUpdateTeamMember(user);

    apiCall(user)
      .then((data: IGenericResponse) => {
        if (data.success) setHasSuccessfullyUpdated(true);
        else alert(data.message);
      })
      .catch((err: Error) => {
        alert('Failed to update team at the moment. Please try again later');
        console.error('Failed to update team', err);
      })
      .finally(() => setIsLoading(false));
  };

  const handleOpenPermissionsEdit = (
    user: ISupplierUserInput,
  ): Promise<IGenericResponse> => {
    return apiFindTeamMember(user.email!).then((teamMember) => {
      if (teamMember) {
        const type = {
          panelType: ESidePanelView.EDIT_TEAM_MEMBER_PERMISSIONS,
          data: teamMember,
        };
        reduxToggleSidePanel(true, type, false);
      }
      return {success: true, message: ''};
    });
  };

  const getUserInput = (
    user?: ISupplierUserProfile,
  ): ISupplierUserInput | undefined => {
    if (!user) return undefined;
    return {
      ...user,
      depotIds: user.depots?.map((depot) => depot.id) || [],
    };
  };

  const teamLink = props.loggedInUser.isAdmin ? '/company/team' : '/team';

  const permissions = new PermissionsService(props.loggedInUser);
  const canWrite = permissions.hasPermission(
    ESupplierUserPermissions.TEAM_WRITE,
  );

  if (hasSuccessfullyUpdated) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
              <div className="success-msg">
                {isAdding
                  ? 'Team member added successfully'
                  : 'Team member updated successfully'}
              </div>
            </div>
            <div className="btn-container">
              <Button
                className="primary-btn"
                type="submit"
                variant="outlined"
                color="primary"
                disabled={isLoading}
                onClick={() => {
                  reduxToggleSidePanel(false, null);
                  NavigationService.navigate('/');
                  setTimeout(() => NavigationService.navigate(teamLink), 50);
                }}
              >
                View Team
              </Button>
            </div>
          </div>
        )}
      />
    );
  }

  return (
    <UserForm
      loggedInUser={props.loggedInUser}
      user={getUserInput(props.user)}
      isLoadingDepots={false}
      depots={props.depots}
      defaultDepots={props.loggedInUser.depots}
      isLoading={isLoading}
      submitButtonTitle={isAdding ? 'Add' : 'Update'}
      onSubmit={handleSubmit}
      cancelButtonTitle={'Cancel'}
      onCancel={() => reduxToggleSidePanel(false)}
      shouldCapturePassword={isAdding}
      hideDepotFields={!props.loggedInUser.isAdmin}
      readonly={!canWrite}
    />
  );
};
export default AddOrUpdateTeamMember;
